import type { CompanyEntityData } from '@/components/ui/design-system/entities/company';
import type { JobEntityData } from '@/components/ui/design-system/entities/job';
import type { ContactEntityData } from '@/components/ui/design-system/entities/contact';
import type {
  ClassifiedQuery,
  JobWithContacts,
  SearchPagers,
  TopMatch,
} from '../types';

/**
 * Phase-1 sample data + simulated pagers. Swapped for real `/api/search/*`
 * calls in Phase 2; the public surface (`buildMockSnapshot` + `mockPagers`)
 * stays identical so the UI doesn't change shape.
 */

export const MOCK_QUERY = 'Series-B fintech in NYC hiring backend engineers';

export const MOCK_CLASSIFICATION: ClassifiedQuery = {
  raw: MOCK_QUERY,
  intent: 'jobs',
  companies: [],
  roles: ['Backend Engineer', 'Senior Backend Engineer', 'Staff Engineer'],
  locations: ['New York, NY', 'Remote (US)'],
  industries: ['Fintech', 'Financial Services'],
  seniority: ['Mid', 'Senior', 'Staff'],
};

/* ------------------------------ Companies ------------------------------- */

const ALL_COMPANIES: CompanyEntityData[] = [
  {
    id: 'co-1',
    name: 'Ramp',
    domain: 'ramp.com',
    industry: 'Fintech',
    employeeCount: 800,
    location: 'New York, NY',
    description: 'Corporate cards and finance automation. Series-D but profile fits — aggressive backend hiring this quarter.',
    website: 'ramp.com',
    isExactMatch: true,
    jobsCount: 12,
    contactsCount: 47,
  },
  {
    id: 'co-3',
    name: 'Alloy',
    domain: 'alloy.com',
    industry: 'Fintech',
    employeeCount: 350,
    location: 'New York, NY',
    description: 'Identity decisioning platform for financial institutions. Series-C; backend-heavy.',
    website: 'alloy.com',
    isExactMatch: true,
    jobsCount: 6,
    contactsCount: 22,
  },
  {
    id: 'co-2',
    name: 'Mercury',
    domain: 'mercury.com',
    industry: 'Fintech',
    employeeCount: 600,
    location: 'San Francisco, CA · Remote',
    description: 'Banking for startups. Strong remote culture; 60% of engineering is non-NYC but core team is hybrid in SF.',
    website: 'mercury.com',
    jobsCount: 8,
    contactsCount: 31,
  },
  {
    id: 'co-4',
    name: 'Unit',
    domain: 'unit.co',
    industry: 'Fintech',
    employeeCount: 200,
    location: 'New York, NY',
    description: 'Banking-as-a-service infrastructure. Smaller team but technical bar is high.',
    jobsCount: 4,
    contactsCount: 14,
  },
  {
    id: 'co-5',
    name: 'Lithic',
    domain: 'lithic.com',
    industry: 'Fintech',
    employeeCount: 180,
    location: 'New York, NY · Remote',
    description: 'Card issuing API. Heavy Go shop, payments domain.',
    jobsCount: 3,
    contactsCount: 11,
  },
  {
    id: 'co-6',
    name: 'Plaid',
    domain: 'plaid.com',
    industry: 'Fintech',
    employeeCount: 1200,
    location: 'San Francisco, CA · NY · Remote',
    description: 'Financial data network. Larger now but Series-B alumni network is dense in NYC.',
    jobsCount: 9,
    contactsCount: 53,
  },
  {
    id: 'co-7',
    name: 'Stash',
    domain: 'stash.com',
    industry: 'Fintech',
    employeeCount: 500,
    location: 'New York, NY',
    description: 'Consumer investing platform. Mature engineering org with strong backend mentorship culture.',
    jobsCount: 5,
    contactsCount: 19,
  },
];

const TOP_MATCH_COMPANY_IDS = new Set(['co-1', 'co-3', 'co-2']);

/* ------------------------------- Contacts ------------------------------- */

const ALL_CONTACTS: ContactEntityData[] = [
  // Ramp
  { id: 'c-1', fullName: 'Maya Chen', title: 'VP Engineering', company: { name: 'Ramp' }, location: 'New York, NY', seniority: 'VP', relevanceScore: 0.92, relevanceReason: 'Owns Payments org, hired 6 backend engineers in the last quarter.', linkedInUrl: 'https://linkedin.com/in/example', tags: ['Hiring Manager', 'Payments'] },
  { id: 'c-2', fullName: 'Daniel Park', title: 'Engineering Manager, Ledger', company: { name: 'Ramp' }, location: 'New York, NY', seniority: 'Manager', relevanceScore: 0.86, relevanceReason: 'Direct manager for the open Payments role.', tags: ['Hiring Manager'] },
  { id: 'c-1b', fullName: 'Inés Ortega', title: 'Senior Recruiter, Engineering', company: { name: 'Ramp' }, location: 'New York, NY', seniority: 'IC', relevanceScore: 0.81, relevanceReason: 'Engineering-focused recruiter — usually first conversation.', tags: ['Recruiter'] },
  { id: 'c-1c', fullName: 'Felix Wang', title: 'Staff Engineer, Reconciliation', company: { name: 'Ramp' }, location: 'New York, NY', seniority: 'IC5', relevanceScore: 0.77, relevanceReason: 'Tech lead on the team; usually a panel interviewer.', tags: ['IC'] },
  { id: 'c-1d', fullName: 'Alex Rivera', title: 'Engineering Director, Bill Pay', company: { name: 'Ramp' }, location: 'New York, NY', seniority: 'Director', relevanceScore: 0.75, relevanceReason: 'Skip-level for the Payments team.', tags: ['Decision Maker'] },
  // Alloy
  { id: 'c-3', fullName: 'Priya Natarajan', title: 'Director of Engineering', company: { name: 'Alloy' }, location: 'New York, NY', seniority: 'Director', relevanceScore: 0.78, relevanceReason: 'Leads Core Platform — direct report manager.', tags: ['Hiring Manager', 'Platform'] },
  { id: 'c-3b', fullName: 'Rashid Ali', title: 'Staff Engineer, Rules Engine', company: { name: 'Alloy' }, location: 'New York, NY', seniority: 'IC5', relevanceScore: 0.74, relevanceReason: 'Tech lead on the rules-engine rebuild.', tags: ['IC'] },
  { id: 'c-3c', fullName: 'Naomi Klein', title: 'Engineering Manager, Identity', company: { name: 'Alloy' }, location: 'New York, NY', seniority: 'Manager', relevanceScore: 0.7, relevanceReason: 'Hiring manager for the Identity team.', tags: ['Hiring Manager'] },
  { id: 'c-3d', fullName: 'Hannah Liu', title: 'Talent Partner', company: { name: 'Alloy' }, location: 'New York, NY', seniority: 'IC', relevanceScore: 0.66, relevanceReason: 'Recruiting partner for engineering.', tags: ['Recruiter'] },
  // Mercury
  { id: 'c-4', fullName: 'Jordan Reyes', title: 'Staff Engineer', company: { name: 'Mercury' }, location: 'Remote', seniority: 'IC5', relevanceScore: 0.72, relevanceReason: 'Tech lead on Banking APIs; would be a peer interviewer.', tags: ['IC', 'Banking'] },
  { id: 'c-4b', fullName: 'Lena Park', title: 'Engineering Manager, Banking APIs', company: { name: 'Mercury' }, location: 'San Francisco, CA', seniority: 'Manager', relevanceScore: 0.79, relevanceReason: 'Hiring manager for the Banking APIs role.', tags: ['Hiring Manager'] },
  { id: 'c-4c', fullName: 'Aria Watson', title: 'VP Platform', company: { name: 'Mercury' }, location: 'San Francisco, CA', seniority: 'VP', relevanceScore: 0.74, relevanceReason: 'Owns the Platform org; final decision maker for senior hires.', tags: ['Decision Maker'] },
  { id: 'c-4d', fullName: 'Kenji Sato', title: 'Senior Recruiter', company: { name: 'Mercury' }, location: 'Remote', seniority: 'IC', relevanceScore: 0.65, relevanceReason: 'Engineering recruiter; typically the first call.', tags: ['Recruiter'] },
  // Unit, Plaid, Lithic, Stash (non-top-match)
  { id: 'c-6', fullName: 'Marcus Webb', title: 'CTO', company: { name: 'Unit' }, location: 'New York, NY', seniority: 'C-Level', relevanceScore: 0.58, relevanceReason: 'Smaller co; CTO is reachable and approves senior eng hires personally.', tags: ['Decision Maker'] },
  { id: 'c-7', fullName: 'Sasha Gupta', title: 'Engineering Manager, Risk', company: { name: 'Unit' }, location: 'New York, NY', seniority: 'Manager', relevanceScore: 0.74, relevanceReason: 'Hiring manager for Backend Engineer, Risk.', tags: ['Hiring Manager'] },
  { id: 'c-5', fullName: 'Aisha Brooks', title: 'Recruiter, Engineering', company: { name: 'Plaid' }, location: 'New York, NY', seniority: 'IC', relevanceScore: 0.65, relevanceReason: 'Eng-focused recruiter at Plaid; warm intro path.', tags: ['Recruiter'] },
  { id: 'c-9', fullName: 'Emily Vasquez', title: 'Staff Engineer, Card Infra', company: { name: 'Lithic' }, location: 'Remote', seniority: 'IC5', relevanceScore: 0.71, relevanceReason: 'Peer interviewer for the Card Infra role; active on Go OSS.', tags: ['IC', 'Payments'] },
  { id: 'c-8', fullName: 'Tomás Reyna', title: 'Director of Platform', company: { name: 'Stash' }, location: 'New York, NY', seniority: 'Director', relevanceScore: 0.69, relevanceReason: 'Owns Money Movement; warm intro path via Plaid alums.', tags: ['Decision Maker'] },
];

/* --------------------------------- Jobs --------------------------------- */

const ALL_JOBS: JobEntityData[] = [
  // Ramp
  { id: 'job-1', title: 'Senior Backend Engineer, Payments', company: { name: 'Ramp' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), description: 'Own the ledger and reconciliation systems behind Ramp Bill Pay. Go + Postgres.', categoryLabel: 'Engineering' },
  { id: 'job-1b', title: 'Backend Engineer, Bill Pay', company: { name: 'Ramp' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4), description: 'Build the next generation of bill-pay automation. Go service, Postgres.', categoryLabel: 'Engineering' },
  { id: 'job-1c', title: 'Senior Engineer, Card Issuing', company: { name: 'Ramp' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6), description: 'Lead our card-issuing flows end-to-end.', categoryLabel: 'Engineering' },
  // Alloy
  { id: 'job-2', title: 'Staff Engineer, Core Platform', company: { name: 'Alloy' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), description: 'Lead the team rebuilding the rules engine. Java/Kotlin, high-throughput.', categoryLabel: 'Engineering' },
  { id: 'job-8', title: 'Backend Engineer, Identity', company: { name: 'Alloy' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8), description: 'Second eng on the identity-graph rebuild. Heavy schema design.', categoryLabel: 'Engineering' },
  { id: 'job-2c', title: 'Senior Engineer, Decisioning', company: { name: 'Alloy' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 9), description: 'Build the decisioning service that backs the rules engine.', categoryLabel: 'Engineering' },
  // Mercury
  { id: 'job-3', title: 'Backend Engineer, Banking APIs', company: { name: 'Mercury' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }, { city: 'Remote', state: '', remote: true, key: 'remote' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1), description: 'Build out the Mercury Bank infra. Python and Postgres.', categoryLabel: 'Engineering' },
  { id: 'job-3b', title: 'Senior Backend Engineer, Treasury', company: { name: 'Mercury' }, locations: [{ city: 'Remote', state: '', remote: true, key: 'remote' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), description: 'Treasury services for Mercury\'s business banking customers.', categoryLabel: 'Engineering' },
  // Other companies (used in More Jobs)
  { id: 'job-4', title: 'Senior Software Engineer, Card Infra', company: { name: 'Lithic' }, locations: [{ city: 'Remote', state: '', remote: true, key: 'remote' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7), description: 'Card issuing platform. Go, very payments-focused.', categoryLabel: 'Engineering' },
  { id: 'job-5', title: 'Backend Engineer, Risk', company: { name: 'Unit' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3), description: 'Design fraud and risk detection services for the BaaS layer.', categoryLabel: 'Engineering' },
  { id: 'job-6', title: 'Backend Engineer, Data Platform', company: { name: 'Plaid' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4), description: 'Scale the financial-data ingest pipeline. Python, Kafka, Spark.', categoryLabel: 'Engineering' },
  { id: 'job-7', title: 'Senior Backend Engineer, Money Movement', company: { name: 'Stash' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6), description: 'Own ACH and settlement workflows. Strong async/queue background helpful.', categoryLabel: 'Engineering' },
  { id: 'job-9', title: 'Backend Engineer, Linking', company: { name: 'Plaid' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 9), description: 'Maintain the bank-link integrations. Go, deep payments domain.', categoryLabel: 'Engineering' },
  { id: 'job-10', title: 'Backend Engineer, Risk Identity', company: { name: 'Unit' }, locations: [{ city: 'New York', state: 'NY', remote: false, key: 'new-york-ny' }], postedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10), description: 'Pair with Risk to build identity-correlation features.', categoryLabel: 'Engineering' },
];

/* ---------------------------- Top matches ------------------------------- */

const TOP_MATCH_INITIAL_CONTACTS = 2;
const TOP_MATCH_INITIAL_JOBS = 2;

function contactsAtCompany(companyName: string): ContactEntityData[] {
  return ALL_CONTACTS.filter((c) => c.company?.name === companyName).sort(
    (a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0),
  );
}

function jobsAtCompany(companyName: string): JobEntityData[] {
  return ALL_JOBS.filter((j) => j.company.name === companyName);
}

export const MOCK_TOP_MATCHES: TopMatch[] = ALL_COMPANIES.filter((c) =>
  TOP_MATCH_COMPANY_IDS.has(c.id),
).map((company) => {
  const contacts = contactsAtCompany(company.name);
  const jobs = jobsAtCompany(company.name);
  return {
    company,
    contacts: contacts.slice(0, TOP_MATCH_INITIAL_CONTACTS),
    jobs: jobs.slice(0, TOP_MATCH_INITIAL_JOBS),
    totalContacts: contacts.length,
    totalJobs: jobs.length,
  };
});

/* --------------------- Flat sections (more jobs/contacts) ----------------- */

/**
 * "More Jobs" pool: jobs whose company is NOT in the top matches. For each
 * job we pre-attach 1 hiring contact at the same company so the inline
 * "people at X" mini-cluster works without an extra fetch.
 */
const MORE_JOBS_POOL: JobWithContacts[] = ALL_JOBS.filter(
  (job) => !MOCK_TOP_MATCHES.some((m) => m.company.name === job.company.name),
).map((job) => {
  const attached = ALL_CONTACTS.filter(
    (c) => c.company?.name === job.company.name && (c.relevanceScore ?? 0) >= 0.6,
  ).slice(0, 1);
  return { job, contacts: attached };
});

/**
 * "More Contacts" pool: contacts NOT at top-match companies AND not already
 * surfaced under a More Jobs item.
 */
const MORE_CONTACTS_POOL: ContactEntityData[] = ALL_CONTACTS.filter(
  (contact) =>
    !MOCK_TOP_MATCHES.some((m) => m.company.name === contact.company?.name) &&
    !MORE_JOBS_POOL.some((j) => j.contacts.some((c) => c.id === contact.id)),
);

const MORE_JOBS_INITIAL = 3;
const MORE_CONTACTS_INITIAL = 3;

export const MOCK_MORE_JOBS_INITIAL = MORE_JOBS_POOL.slice(0, MORE_JOBS_INITIAL);
export const MOCK_MORE_JOBS_TOTAL = MORE_JOBS_POOL.length;
export const MOCK_MORE_CONTACTS_INITIAL = MORE_CONTACTS_POOL.slice(0, MORE_CONTACTS_INITIAL);
export const MOCK_MORE_CONTACTS_TOTAL = MORE_CONTACTS_POOL.length;

/* ---------------------------- Pagers (mocks) ---------------------------- */

const FAKE_LATENCY_MS = 450;
const PAGE_SIZE = 3;

function fakeLatency<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), FAKE_LATENCY_MS));
}

export const mockPagers: SearchPagers = {
  loadMoreCompanyContacts: (companyId, offset) => {
    const company = ALL_COMPANIES.find((c) => c.id === companyId);
    if (!company) return fakeLatency([]);
    const slice = contactsAtCompany(company.name).slice(offset, offset + PAGE_SIZE);
    return fakeLatency(slice);
  },
  loadMoreCompanyJobs: (companyId, offset) => {
    const company = ALL_COMPANIES.find((c) => c.id === companyId);
    if (!company) return fakeLatency([]);
    const slice = jobsAtCompany(company.name).slice(offset, offset + PAGE_SIZE);
    return fakeLatency(slice);
  },
  loadMoreJobs: (offset) => fakeLatency(MORE_JOBS_POOL.slice(offset, offset + PAGE_SIZE)),
  loadMoreContacts: (offset) =>
    fakeLatency(MORE_CONTACTS_POOL.slice(offset, offset + PAGE_SIZE)),
};

/* ------------------------------- Insights ------------------------------- */

export const MOCK_INSIGHTS = {
  top: 'Reading this as: senior-leaning backend roles at Series-B-sized fintech companies, NYC-anchored but open to remote-friendly East Coast teams.',
  betweenMatchesAndJobs:
    'These three companies between them already cover most of the openings you\'d care about — the rest below are at smaller or more remote-heavy teams.',
  bottom: 'Warm intros via mutual connections at Plaid will likely outperform cold outreach for the smaller companies on this list.',
} as const;

export const MOCK_TOP_MATCHES_COMMENTARY =
  'Top picks anchored on the companies most likely to convert. Each card opens to reveal nearby people and openings — same query, deeper view.';

export const MOCK_JOBS_COMMENTARY =
  'Openings at companies that didn\'t make the top three but still match the brief. Each row shows a lead contact at the same company when we have one.';

export const MOCK_CONTACTS_COMMENTARY =
  'People worth reaching out to who aren\'t already attached to a job above — useful as warm-intro starting points.';

const ALL_SUGGESTIONS = [
  'Software engineer jobs in New York',
  'Remote product manager roles',
  'Marketing managers at tech startups',
  'Sales leaders in San Francisco',
  'Frontend developer jobs in Austin',
  'HR managers hiring in Chicago',
  'Data scientists at healthcare companies',
  'DevOps engineers at fintech startups',
  'UX designers at e-commerce companies',
  'Operations managers at logistics firms',
  'Backend engineers at Series-A startups',
  'Growth marketers at SaaS companies',
  'Product designers working remotely',
  'Account executives in New York',
  'Machine learning engineers in Seattle',
  'Customer success managers at B2B companies',
  'Finance managers at mid-size companies',
  'Recruiters at tech companies',
  'Content strategists at media companies',
  'iOS developers at consumer apps',
  'Android engineers in Los Angeles',
  'Security engineers at enterprise companies',
  'Business analysts at consulting firms',
  'Brand designers at agencies',
  'Product managers at marketplace startups',
  'Engineering managers in Boston',
  'Data analysts at retail companies',
  'QA engineers at gaming companies',
  'Full-stack developers in Miami',
  'CFOs at Series-B companies',
  'Supply chain managers at manufacturing firms',
  'Legal counsels at tech companies',
  'Social media managers at consumer brands',
  'Solutions architects at cloud companies',
  'Technical program managers in Denver',
  'Biotech companies hiring in San Diego',
  'Climate tech startups hiring engineers',
  'Fintech companies with open roles in NYC',
  'AI startups hiring product managers',
  'Health tech companies in Boston',
  'EdTech companies hiring designers',
  'Real estate tech firms in Dallas',
  'Cybersecurity companies in Washington DC',
  'Robotics companies in Detroit',
  'Media companies hiring in Los Angeles',
  'Retail tech companies hiring in Seattle',
  'Travel startups with remote positions',
  'Food tech companies hiring in Chicago',
  'Companies with strong engineering culture',
  'Startups hiring senior leaders this quarter',
] as const;

export function pickRandomSuggestions(count = 4): string[] {
  const shuffled = [...ALL_SUGGESTIONS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}
